import React from 'react';
import styles from './PaginatedThemePicker.scss';

type HeartParticle = {
  id: number;
  leftPx: number;
  delayMs: number;
  scale: number;
};

const PIXEL_HEART: Array<[number, number]> = [
  [2, 1],
  [3, 1],
  [5, 1],
  [6, 1],
  [1, 2],
  [2, 2],
  [3, 2],
  [4, 2],
  [5, 2],
  [6, 2],
  [7, 2],
  [1, 3],
  [2, 3],
  [3, 3],
  [4, 3],
  [5, 3],
  [6, 3],
  [7, 3],
  [2, 4],
  [3, 4],
  [4, 4],
  [5, 4],
  [6, 4],
  [3, 5],
  [4, 5],
  [5, 5],
  [4, 6],
];

export const EndOfListMessage: React.FC = () => {
  const [particles, setParticles] = React.useState<HeartParticle[]>([]);
  const idRef = React.useRef(1);

  const spawnHearts = React.useCallback(() => {
    const baseId = idRef.current;
    idRef.current += 8;

    const next: HeartParticle[] = Array.from({ length: 8 }).map((_, idx) => ({
      id: baseId + idx,
      // spread around center a bit
      leftPx: Math.round((Math.random() - 0.5) * 70),
      delayMs: Math.round(Math.random() * 140),
      scale: 0.7 + Math.random() * 0.6,
    }));

    setParticles(prev => [...prev, ...next]);
  }, []);

  const removeParticle = React.useCallback((id: number) => {
    setParticles(prev => prev.filter(p => p.id !== id));
  }, []);

  return (
    <div className={styles.endOfList} data-testid="end-of-list">
      <style>{`
        .eolmWrap {
          position: relative;
          display: inline-block;
        }
        .eolmBurstArea {
          position: relative;
          display: flex;
          justify-content: center;
          margin: 16px 0;
        }
        .eolmParticle {
          position: absolute;
          left: 50%;
          bottom: 8px;
          pointer-events: none;
          opacity: 0;
          transform: translateX(-50%);
          animation: eolmFloat 900ms ease-out forwards;
          will-change: transform, opacity;
          filter: drop-shadow(0 1px 0 rgba(0,0,0,0.08));
        }
        @keyframes eolmFloat {
          0%   { opacity: 0; transform: translateX(calc(-50% + var(--x))) translateY(0) scale(var(--s)); }
          10%  { opacity: 1; }
          100% { opacity: 0; transform: translateX(calc(-50% + var(--x))) translateY(-56px) scale(var(--s)); }
        }
      `}</style>

      <div className="eolmBurstArea" aria-hidden="true" onMouseEnter={spawnHearts}>
        <span className="eolmWrap">
          {/* Particles */}
          {particles.map(p => (
            <span
              key={p.id}
              className="eolmParticle"
              style={
                {
                  '--x': `${p.leftPx}px`,
                  '--s': p.scale,
                  animationDelay: `${p.delayMs}ms`,
                } as React.CSSProperties
              }
              onAnimationEnd={() => removeParticle(p.id)}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 16 16"
                style={{ imageRendering: 'pixelated' }}
                aria-hidden="true"
              >
                {PIXEL_HEART.map(([x, y], i) => (
                  <rect key={i} x={x} y={y} width="1" height="1" fill="#FF44CB" />
                ))}
              </svg>
            </span>
          ))}

          {/* Main heart */}
          <svg
            width="48"
            height="48"
            viewBox="0 0 16 16"
            role="img"
            aria-label="8-bit heart"
            style={{ imageRendering: 'pixelated' }}
          >
            {PIXEL_HEART.map(([x, y], i) => (
              <rect key={i} x={x} y={y} width="1" height="1" fill="#FF44CB" />
            ))}
          </svg>
        </span>
      </div>

      <p style={{ textAlign: 'center' }}>You&apos;ve reached the end.</p>
    </div>
  );
};
